import React, { useState } from 'react';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';

interface CollapsibleResultGroupProps {
  /** Leading emoji/icon for the product type, e.g. "🏨". */
  icon: string;
  /** Localized product noun phrase, e.g. "Hotel options". */
  title: string;
  /** How many options the (now collapsed) list held. */
  count: number;
  /** Human label of the option the user picked, e.g. "Hilton". Optional —
   *  absent when no selection was captured for this list. */
  pickLabel?: string;
  /** Start expanded? Defaults to collapsed (the superseded-list use case). */
  defaultExpanded?: boolean;
  /**
   * Dim the cards and kill their controls (the superseded-list default). Pass
   * `false` to host a LIVE list — the two-one-way sections use the group only
   * for its heading/collapse chrome and need their Select buttons to work.
   */
  viewOnly?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps a superseded option list (search results / rooms / tickets) in a
 * collapsed, view-only group. The list lives on forever in the transcript:
 * the user can expand it to preview the options (cards render disabled — see
 * the `.bot-results-group--disabled` styling) but can no longer select.
 *
 * The body is mounted only while expanded, so collapsed groups cost nothing.
 */
const CollapsibleResultGroup: React.FC<CollapsibleResultGroupProps> = ({
  icon,
  title,
  count,
  pickLabel,
  defaultExpanded = false,
  viewOnly = true,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const youChose = t?.collapsed?.youChose ?? 'Your pick:';

  return (
    <div
      className={`bot-results-group${viewOnly ? ' bot-results-group--disabled' : ''}`}
    >
      <button
        type="button"
        className="bot-results-group__header"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="bot-results-group__icon">{icon}</span>
        <span className="bot-results-group__title">
          {title} ({count})
        </span>
        <span className="bot-results-group__chevron">
          {expanded ? <IoChevronDown /> : <IoChevronForward />}
        </span>
      </button>
      {expanded && <div className="bot-results-group__body">{children}</div>}
      {pickLabel && (
        <div className="bot-results-group__pick">
          {youChose} <strong>{pickLabel}</strong>
        </div>
      )}
    </div>
  );
};

export default CollapsibleResultGroup;
